// Zoom Apps SDK の薄いラッパ。dev モード (= Zoom 外、ブラウザ単体) では
// URL パラメータでロールを切り替えられるモックを返す。これにより
// Zoom にサイドロードしなくても apps/in-client を `vite dev` で動作確認できる。
//
// 使い方:
//   const ctx = await initZoomApp();
//   ctx.role        → 'host' | 'participant' | 'coHost'
//   ctx.meetingId   → 'mock-meeting-1' (dev) or 実 meeting ID
//   ctx.userId      → 'mock-user-1' (dev) or 実 participant UUID

import zoomSdk from '@zoom/appssdk';
import { IS_DEV } from '../env.ts';

export type Role = 'host' | 'coHost' | 'participant';

export interface AppContext {
  role: Role;
  meetingId: string;
  userId: string;
  isMock: boolean;
}

const ZOOM_SDK_CAPABILITIES = [
  'getMeetingContext',
  'getRunningContext',
  'getUserContext',
] as const;

export async function initZoomApp(): Promise<AppContext> {
  // ?mock= が URL についていれば常にモック (test/dev 用)。
  // 本番では URL params を細工できないので副作用なし。
  if (shouldUseMock()) {
    return mockContext();
  }
  try {
    await zoomSdk.config({
      capabilities: [...ZOOM_SDK_CAPABILITIES],
      version: '0.16',
    });
    const meeting = await zoomSdk.getMeetingContext();
    const user = await zoomSdk.getUserContext();
    return {
      role: normalizeRole(user.role),
      meetingId: meeting.meetingID,
      userId: user.participantUUID,
      isMock: false,
    };
  } catch (e) {
    if (IS_DEV) {
      // dev で Zoom 外実行時は SDK が落ちる → モックにフォールバック
      console.warn('[zoom-sdk] falling back to mock context:', e);
      return mockContext();
    }
    throw e;
  }
}

function shouldUseMock(): boolean {
  if (typeof window === 'undefined') return true;
  return new URLSearchParams(window.location.search).has('mock');
}

function mockContext(): AppContext {
  const params =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  const role = (params.get('role') ?? 'participant') as Role;
  return {
    role: normalizeRole(role),
    meetingId: params.get('meetingId') ?? 'mock-meeting-1',
    userId: params.get('userId') ?? `mock-${role}-1`,
    isMock: true,
  };
}

function normalizeRole(raw: string): Role {
  if (raw === 'host' || raw === 'Host') return 'host';
  if (raw === 'coHost' || raw === 'Co-host' || raw === 'co-host') return 'coHost';
  return 'participant';
}
